import React from 'react';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { 
  ZoomIn, 
  ZoomOut, 
  Move, 
  Maximize, 
  Minimize,
  RotateCcw,
  Trash2,
  Copy,
  X
} from 'lucide-react';

export default function ImageEditPanel({ 
  position,
  imageLayer,
  onUpdateImage,
  onRemoveImage,
  onApplyToAll,
  onClose
}) {
  if (!imageLayer) return null;

  const {
    scale = 1,
    offsetX = 0,
    offsetY = 0,
    fitMode = 'cover' // 'cover' (fill) or 'contain' (fit)
  } = imageLayer;

  const handleScaleChange = (value) => {
    onUpdateImage({ ...imageLayer, scale: value });
  };

  const handleOffsetChange = (axis, value) => {
    if (axis === 'x') {
      onUpdateImage({ ...imageLayer, offsetX: value });
    } else {
      onUpdateImage({ ...imageLayer, offsetY: value });
    }
  };

  const handleFitModeToggle = () => {
    const newMode = fitMode === 'cover' ? 'contain' : 'cover';
    onUpdateImage({ ...imageLayer, fitMode: newMode, scale: 1, offsetX: 0, offsetY: 0 });
  };

  const handleReset = () => {
    onUpdateImage({ ...imageLayer, scale: 1, offsetX: 0, offsetY: 0 });
  };

  const scalePercent = Math.round(scale * 100);

  return (
    <div 
      className="absolute z-50 bg-card/95 backdrop-blur-sm border border-border rounded-2xl shadow-lg p-4 w-72"
      style={{
        left: position.x,
        top: position.y,
        transform: 'translateX(-50%)'
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-sm">Edit Image</h4>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 rounded-lg"
          onClick={onClose}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Fit Mode Toggle */}
      <div className="mb-4">
        <label className="text-xs text-muted-foreground mb-2 block">Fit Mode</label>
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant={fitMode === 'cover' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onUpdateImage({ ...imageLayer, fitMode: 'cover', scale: 1, offsetX: 0, offsetY: 0 })}
            className="rounded-xl text-xs"
          >
            <Maximize className="w-3 h-3 mr-1" />
            Fill
          </Button>
          <Button
            variant={fitMode === 'contain' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onUpdateImage({ ...imageLayer, fitMode: 'contain', scale: 1, offsetX: 0, offsetY: 0 })}
            className="rounded-xl text-xs"
          >
            <Minimize className="w-3 h-3 mr-1" />
            Fit
          </Button>
        </div>
      </div>

      {/* Zoom/Scale Control */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs text-muted-foreground">Zoom</label>
          <span className="text-xs font-medium bg-muted px-2 py-0.5 rounded-md">{scalePercent}%</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 rounded-lg"
            onClick={() => handleScaleChange(Math.max(0.5, scale - 0.1))}
            disabled={scale <= 0.5}
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Slider
            value={[scale * 100]}
            onValueChange={([value]) => handleScaleChange(value / 100)}
            min={50}
            max={300}
            step={5}
            className="flex-1"
          />
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 rounded-lg"
            onClick={() => handleScaleChange(Math.min(3, scale + 0.1))}
            disabled={scale >= 3}
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Position Controls */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Move className="w-3 h-3 text-muted-foreground" />
          <label className="text-xs text-muted-foreground">Position</label>
        </div>
        
        {/* Horizontal Position */}
        <div className="mb-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Horizontal</span>
            <span className="text-xs font-medium">{offsetX}%</span>
          </div>
          <Slider
            value={[offsetX + 100]}
            onValueChange={([value]) => handleOffsetChange('x', value - 100)}
            min={0}
            max={200}
            step={1}
            className="w-full"
          />
        </div>

        {/* Vertical Position */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Vertical</span>
            <span className="text-xs font-medium">{offsetY}%</span>
          </div>
          <Slider
            value={[offsetY + 100]}
            onValueChange={([value]) => handleOffsetChange('y', value - 100)}
            min={0}
            max={200}
            step={1}
            className="w-full"
          />
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-border my-3" />

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 rounded-xl text-xs"
          onClick={handleReset}
        >
          <RotateCcw className="w-3 h-3 mr-1" />
          Reset
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1 rounded-xl text-xs"
          onClick={onApplyToAll}
        >
          <Copy className="w-3 h-3 mr-1" />
          Apply All
        </Button>
      </div>

      <Button
        variant="destructive"
        size="sm"
        className="w-full mt-2 rounded-xl text-xs"
        onClick={onRemoveImage}
      >
        <Trash2 className="w-3 h-3 mr-1" />
        Remove Image
      </Button>

      {/* Tips */}
      <div className="mt-3 space-y-1">
        <p className="text-xs text-muted-foreground text-center">
          💡 Drag on canvas to reposition
        </p>
        <p className="text-xs text-green-600 text-center font-medium">
          ✂️ Auto-crop to cutting line on export
        </p>
      </div>
    </div>
  );
}
